import React, { useEffect, useState } from 'react';
import { Button, MenuItem, TableCell, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../../../contexts/AuthContext';
import { lotService } from '../../../inventory/services/lotService';
import type { LotListItem } from '../../../inventory/types/lots.types';
import type { InvoiceItem } from '../types/invoice.types';

export type InvoiceLotMode = 'purchase' | 'sale' | 'purchaseReturn' | 'saleReturn';

export function useShowInvoiceLots(items: Array<{ trackLots?: boolean; lotNo?: string }>): boolean {
  const { isModuleEnabled } = useAuth();
  const enabled = typeof isModuleEnabled === 'function' && isModuleEnabled('lot_tracking');
  return enabled && items.some((item) => item.trackLots === true || Boolean(item.lotNo?.trim()));
}

const today = () => new Date().toISOString().slice(0, 10);

interface Props {
  item: InvoiceItem;
  mode: InvoiceLotMode;
  highlight?: boolean;
  onChange: (id: string, patch: Partial<InvoiceItem>) => void;
  onSplit?: (id: string) => void;
}

export const InvoiceLotCells: React.FC<Props> = ({ item, mode, highlight, onChange, onSplit }) => {
  const { t } = useTranslation('lots');
  const [lots, setLots] = useState<LotListItem[]>([]);
  const tracked = item.trackLots === true || Boolean(item.lotNo?.trim());
  const pickLot = (mode === 'sale' || mode === 'purchaseReturn') && tracked && !item.itemId.startsWith('manual');
  const textLot = tracked && (mode === 'purchase' || mode === 'saleReturn');

  useEffect(() => {
    if (!pickLot) return;
    let alive = true;
    lotService.byItem(item.itemId).then((rows) => {
      if (!alive) return;
      const usable = rows
        .filter((row) => row.status !== 'HOLD')
        .sort((a, b) => (a.expiryDate || '9999-99-99').localeCompare(b.expiryDate || '9999-99-99'));
      setLots(usable);
    }).catch(() => setLots([]));
    return () => { alive = false; };
  }, [pickLot, item.itemId]);

  if (!tracked) {
    return (<><TableCell /><TableCell /><TableCell /></>);
  }

  const outline = highlight ? { '& .MuiOutlinedInput-notchedOutline': { borderColor: 'error.main' } } : undefined;

  return (
    <>
      <TableCell sx={{ py: 0.75, px: 1, minWidth: 140 }}>
        {pickLot && (
          <>
            <TextField
              select
              size="small"
              value={item.lotNo || ''}
              onChange={(e) => {
                const lot = lots.find((row) => row.lotNo === e.target.value);
                onChange(item.id, {
                  lotNo: e.target.value,
                  expiryDate: lot?.expiryDate ? String(lot.expiryDate).slice(0, 10) : item.expiryDate,
                  productionDate: lot?.productionDate ? String(lot.productionDate).slice(0, 10) : item.productionDate,
                });
              }}
              sx={{ width: 150, ...outline }}
            >
              <MenuItem value="">{t('invoice.fefo')}</MenuItem>
              {lots.map((row) => (
                <MenuItem
                  key={row.id}
                  value={row.lotNo}
                  sx={{ color: row.expiryDate && row.expiryDate.slice(0, 10) < today() ? 'error.main' : undefined }}
                >
                  {row.lotNo}{row.expiryDate ? ` · ${row.expiryDate.slice(0, 10)}` : ''} ({row.quantity})
                </MenuItem>
              ))}
            </TextField>
            {onSplit && (
              <Button size="small" onClick={() => onSplit(item.id)} sx={{ display: 'block', mt: 0.5 }}>
                {t('invoice.split')}
              </Button>
            )}
          </>
        )}
        {textLot && (
          <TextField
            size="small"
            value={item.lotNo || ''}
            placeholder={t('invoice.lotNo')}
            onChange={(e) => onChange(item.id, { lotNo: e.target.value })}
            sx={{ width: 140, ...outline }}
          />
        )}
      </TableCell>
      <TableCell sx={{ py: 0.75, px: 1 }}>
        {tracked && (
          <TextField
            size="small"
            type="date"
            value={item.expiryDate || ''}
            onChange={(e) => onChange(item.id, { expiryDate: e.target.value })}
            sx={{ width: 140, ...outline }}
          />
        )}
      </TableCell>
      <TableCell sx={{ py: 0.75, px: 1 }}>
        {tracked && (
          <TextField
            size="small"
            type="date"
            value={item.productionDate || ''}
            onChange={(e) => onChange(item.id, { productionDate: e.target.value })}
            sx={{ width: 140 }}
          />
        )}
      </TableCell>
    </>
  );
};

export const InvoiceLotHeaders: React.FC = () => {
  const { t } = useTranslation('lots');
  const head = { py: 0.5, px: 1, fontSize: '0.75rem', whiteSpace: 'nowrap' as const };
  return (
    <>
      <TableCell sx={head}>{t('invoice.lotNo')}</TableCell>
      <TableCell sx={head}>{t('invoice.expiry')}</TableCell>
      <TableCell sx={head}>{t('invoice.production')}</TableCell>
    </>
  );
};
